import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Package, MapPin, Gift, Settings, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AccountPage = () => {
  const [searchParams] = useSearchParams();
  const { user, token, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const defaultTab = searchParams.get('tab') || 'overview';

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const [ordersRes, loyaltyRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loyalty/balance`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordersRes.data.orders || []);
      setLoyaltyData(loyaltyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="pt-32 pb-24 chaleur-container text-center">
        <h1 className="text-2xl mb-4">Please login to view your account</h1>
        <Button asChild>
          <Link to="/login">Sign In</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24" data-testid="account-page">
      <div className="chaleur-container">
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="text-3xl md:text-4xl font-light mb-2">Hello, {user.first_name}</h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
          <Button variant="outline" onClick={logout} className="rounded-none">
            Sign Out
          </Button>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-8 flex-wrap">
            <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
              Overview
            </TabsTrigger>
            <TabsTrigger value="orders" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
              Orders
            </TabsTrigger>
            <TabsTrigger value="loyalty" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
              Chaleur Club
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-4">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Loyalty Card */}
              <div className="p-6 bg-gradient-to-br from-primary/20 to-accent/20 border border-primary/20">
                <p className="caption text-primary mb-2">Chaleur Club</p>
                <div className="text-3xl font-light mb-1">{loyaltyData?.points || 0}</div>
                <p className="text-sm text-muted-foreground mb-4">Points Balance</p>
                <div className="flex items-center justify-between">
                  <span className="text-sm">{loyaltyData?.tier || 'Bronze'} Member</span>
                  <Link to="/chaleur-club" className="text-primary text-sm hover:underline">
                    View Benefits
                  </Link>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="p-6 border border-border">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5" />
                  <span className="font-medium">Recent Orders</span>
                </div>
                {orders.length > 0 ? (
                  <div className="space-y-3">
                    {orders.slice(0, 3).map(order => (
                      <div key={order.id} className="flex justify-between text-sm">
                        <span>{order.order_number}</span>
                        <span className="text-muted-foreground">{order.status}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No orders yet</p>
                )}
              </div>

              {/* Quick Actions */}
              <div className="p-6 border border-border">
                <p className="font-medium mb-4">Quick Actions</p>
                <div className="space-y-3">
                  <Link to="/track-order" className="flex items-center justify-between text-sm hover:text-primary transition-colors">
                    <span>Track an Order</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <Link to="/gifting" className="flex items-center justify-between text-sm hover:text-primary transition-colors">
                    <span>Find a Gift</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                  <Link to="/contact" className="flex items-center justify-between text-sm hover:text-primary transition-colors">
                    <span>Contact Support</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            {loading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 bg-secondary rounded" />
                ))}
              </div>
            ) : orders.length > 0 ? (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="p-6 border border-border">
                    <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                      <div>
                        <p className="font-medium">{order.order_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(order.total)}</p>
                        <span className={`text-xs px-2 py-1 ${
                          order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                          'bg-secondary text-muted-foreground'
                        }`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="text-sm">
                          <span>{item.product_name}</span>
                          <span className="text-muted-foreground"> × {item.quantity}</span>
                        </div>
                      ))}
                    </div>
                    {order.tracking_number && (
                      <p className="text-sm text-primary mt-4">
                        Tracking: {order.tracking_number}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground mb-4">No orders yet</p>
                <Button asChild>
                  <Link to="/shop">Start Shopping</Link>
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Loyalty Tab */}
          <TabsContent value="loyalty">
            <div className="max-w-2xl">
              <div className="p-8 bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 mb-8">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <p className="caption text-primary mb-2">{loyaltyData?.tier || 'Bronze'} Member</p>
                    <div className="text-4xl font-light">{loyaltyData?.points || 0}</div>
                    <p className="text-muted-foreground">Points Balance</p>
                  </div>
                  <Gift className="w-12 h-12 text-primary" />
                </div>
                {loyaltyData?.next_tier_at && (
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span>Progress to {loyaltyData.tier === 'Bronze' ? 'Silver' : 'Gold'}</span>
                      <span>{loyaltyData.next_tier_at - loyaltyData.points} points to go</span>
                    </div>
                    <div className="h-2 bg-secondary/50 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ width: `${(loyaltyData.points / loyaltyData.next_tier_at) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <h3 className="text-xl font-medium mb-4">Your Benefits</h3>
              <ul className="space-y-3">
                {loyaltyData?.benefits?.map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm">
                    <span className="w-6 h-6 bg-primary/10 text-primary flex items-center justify-center text-xs">✓</span>
                    {benefit}
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Link to="/chaleur-club" className="text-primary hover:underline text-sm">
                  Learn more about Chaleur Club →
                </Link>
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="max-w-xl space-y-8">
              <div>
                <h3 className="text-lg font-medium mb-4">Account Details</h3>
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Name</span>
                    <span>{user.first_name} {user.last_name}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Email</span>
                    <span>{user.email}</span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-border">
                    <span className="text-muted-foreground">Phone</span>
                    <span>{user.phone || 'Not provided'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-4">Privacy</h3>
                <Button variant="outline" className="rounded-none">
                  Download My Data
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AccountPage;
